import { withTranslation } from "react-i18next";
import {
  Box,
  Flex,
  HStack,
  IconButton,
  useDisclosure,
  VStack,
  Link,
  Button,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  Image,
  Container,
} from "@chakra-ui/react";
import { HamburgerIcon } from "@chakra-ui/icons";

interface HeaderProps {
  t: (key: string) => string;
}

const NewHeader = ({ t }: HeaderProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const scrollTo = (id: string) => {
    const element = document.getElementById(id) as HTMLDivElement;
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
      });
    }
    onClose();
  };

  return (
    <Box bg="white" px={4} py={4} shadow="sm">
      <Container maxW="1200px">
        <Flex h={24} alignItems="center" justifyContent="space-between">
          {/* Logo */}
          <Box>
            <Link href="https://pilatropico.com/">
              <Image
                src="/img/svg/WordmarkLogo.svg"
                alt="Pilatrópico"
                height="160px"
                width="auto"
                transition="all 0.3s ease-in-out"
                _hover={{
                  transform: "scale(1.05)",
                  filter: "brightness(1.1)"
                }}
              />
            </Link>
          </Box>

          {/* Desktop Menu */}
          <HStack spacing={8} alignItems="center" display={{ base: "none", lg: "flex" }}>
            <Link
              onClick={() => scrollTo("aboutUs")}
              cursor="pointer"
              color="coral.500"
              fontSize="1.2rem"
              fontWeight="400"
              transition="all 0.3s ease-in-out"
              _hover={{ 
                color: "brand.500", 
                textDecoration: "underline",
                transform: "translateY(-2px)",
                textShadow: "0 2px 4px rgba(218, 114, 106, 0.3)"
              }}
            >
              {t("About")}
            </Link>
            <Link
              onClick={() => scrollTo("calendar")}
              cursor="pointer"
              color="coral.500"
              fontSize="1.2rem"
              fontWeight="400"
              transition="all 0.3s ease-in-out"
              _hover={{ 
                color: "brand.500", 
                textDecoration: "underline",
                transform: "translateY(-2px)",
                textShadow: "0 2px 4px rgba(218, 114, 106, 0.3)"
              }}
            >
              {t("Calendar")}
            </Link>
            <Link
              href="https://pilatropico.punchpass.com/passes"
              color="coral.500"
              fontSize="1.2rem"
              fontWeight="400"
              transition="all 0.3s ease-in-out"
              _hover={{ 
                color: "brand.500", 
                textDecoration: "underline",
                transform: "translateY(-2px)",
                textShadow: "0 2px 4px rgba(218, 114, 106, 0.3)"
              }}
              isExternal
            >
              {t("Packages")}
            </Link>
            <Button
              onClick={() => window.open("https://www.instagram.com/pilatropico.pr", "_blank", "noopener,noreferrer")}
              bg="brand.500"
              color="white"
              fontSize="1.1rem"
              fontWeight="700"
              borderRadius="4px"
              px={6}
              py={3}
              _hover={{
                bg: "brand.600",
                transform: "translateY(-2px)",
              }}
              _active={{
                bg: "brand.600",
                transform: "translateY(0)",
              }}
            >
              {t("Contact")}
            </Button>
          </HStack>

          {/* Mobile Menu Button */}
          <IconButton
            size="lg"
            icon={<HamburgerIcon boxSize={6} />}
            aria-label="Open menu"
            display={{ lg: "none" }}
            onClick={onOpen}
            bg="transparent"
            color="coral.500"
            _hover={{ bg: "gray.100" }}
          />
        </Flex>
      </Container>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose} size="sm">
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader
            fontSize="1.5rem"
            fontWeight="600"
            color="coral.500"
            borderBottomWidth="1px"
          >
            Menu
          </DrawerHeader>
          <DrawerBody>
            <VStack spacing={6} mt={4} align="center">
              <Link
                onClick={() => scrollTo("aboutUs")}
                cursor="pointer"
                color="coral.500"
                fontSize="1.4rem"
                fontWeight="400"
                transition="all 0.3s ease-in-out"
                _hover={{ 
                  color: "brand.500", 
                  textDecoration: "underline",
                  transform: "scale(1.05)",
                  textShadow: "0 2px 4px rgba(218, 114, 106, 0.3)"
                }}
              >
                {t("About")}
              </Link>
              <Link
                onClick={() => scrollTo("calendar")}
                cursor="pointer"
                color="coral.500"
                fontSize="1.4rem"
                fontWeight="400"
                transition="all 0.3s ease-in-out"
                _hover={{ 
                  color: "brand.500", 
                  textDecoration: "underline",
                  transform: "scale(1.05)",
                  textShadow: "0 2px 4px rgba(218, 114, 106, 0.3)"
                }}
              >
                {t("Calendar")}
              </Link>
              <Link
                href="https://pilatropico.punchpass.com/passes"
                color="coral.500"
                fontSize="1.4rem"
                fontWeight="400"
                transition="all 0.3s ease-in-out"
                _hover={{ 
                  color: "brand.500", 
                  textDecoration: "underline",
                  transform: "scale(1.05)",
                  textShadow: "0 2px 4px rgba(218, 114, 106, 0.3)"
                }}
                isExternal
              >
                {t("Packages")}
              </Link>
              <Button
                onClick={() => window.open("https://www.instagram.com/pilatropico.pr", "_blank", "noopener,noreferrer")}
                bg="brand.500"
                color="white"
                fontSize="1.2rem"
                fontWeight="700"
                borderRadius="4px"
                width="auto"
                px={8}
                py={4}
                _hover={{
                  bg: "brand.600",
                  transform: "translateY(-2px)",
                }}
                _active={{
                  bg: "brand.600",
                  transform: "translateY(0)",
                }}
              >
                {t("Contact")}
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default withTranslation()(NewHeader); 